import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mail, User, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare, Trash2 } from 'lucide-react';
import type { PlatformAccessRequest } from '@/pages/admin/access-requests';

interface AccessRequestCardProps {
  request: PlatformAccessRequest;
  onApprove: (request: PlatformAccessRequest) => void | Promise<void>;
  onReject: (request: PlatformAccessRequest) => void;
  onViewDetails: (request: PlatformAccessRequest) => void;
  onDelete: (request: PlatformAccessRequest) => void;
  isProcessing: boolean;
}

export function AccessRequestCard({ request, onApprove, onReject, onViewDetails, onDelete, isProcessing }: AccessRequestCardProps) {
  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      'pending': 'secondary',
      'approved': 'default',
      'rejected': 'destructive'
    };

    const icons = {
      'pending': <AlertCircle className="h-3 w-3" />,
      'approved': <CheckCircle className="h-3 w-3" />,
      'rejected': <XCircle className="h-3 w-3" />
    };

    return (
      <Badge variant={variants[status] || 'outline'} className="flex items-center gap-1">
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">{request.name}</h3>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Mail className="h-3 w-3" />
                  {request.email}
                </div>
              </div>
            </div>
            {getStatusBadge(request.status)}
          </div>

          {/* Message */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-sm line-clamp-2">{request.message}</p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {formatDate(request.createdAt)}
            </div>

            {request.status === 'pending' ? (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onApprove(request)}
                  disabled={isProcessing}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onReject(request)}
                  disabled={isProcessing}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onViewDetails(request)}
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Details
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(request)}
                  disabled={isProcessing}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewDetails(request)}
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                View Details
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
